import SlideoverModal from "src/components/SlideoverModal";
import { RootState, useAppDispatch } from "src/stores";

import { connect } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { queryQueueProps } from "src/interfaces";
import { checkPermission, isAdmin, isManager } from "src/helpers";
import { fetchAllUsers } from "src/stores/user";
import _ from "lodash";
import { clearAllListeners } from "@reduxjs/toolkit";

import { FormInput, FormLabel, FormSelect } from "src/base-components/Form";
import ReactSelect from "src/base-components/ReactSelect";
import DatePicker from "react-datepicker";
import moment from "moment";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import {
  createReminderService,
  fetchReminderByIdService,
  updateReminderService,
  fetchReminderCalendarService,
  fetchQueriesService,
} from "src/services/reminder.services";
import { fetchReminders } from "src/stores/reminderQueueCalender/actions";
import { reminderAPIUrls } from "src/common/apiActions";

interface ComponentProps {
  visible: boolean;
  setVisible: (payload: boolean) => void;
  users: any[];
  fetchUsers: (payload: any) => void;
  authUser: any;
  onSuccess?: (created?: any) => void;
  editMode?: boolean;
  editReminderId?: string;
  editReminderData?: any;
}

function Component(props: ComponentProps) {
  const {
    users,
    fetchUsers,
    setVisible,
    visible,
    authUser,
    onSuccess,
    editMode = false,
    editReminderId,
    editReminderData,
  } = props;
  const navigate = useNavigate();

  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadedAll, setIsLoadedAll] = useState(false);

  const [remindOn, setRemindOn] = useState<Date | null>(null);
  const [remindTime, setRemindTime] = useState<string>("");
  const [snoozeTime, setSnoozeTime] = useState<string>("None");

  const [model, setModel] = useState<any>({});
  const [queriesOptions, setQueriesOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [queriesPage, setQueriesPage] = useState(1);
  const [queriesTotalPages, setQueriesTotalPages] = useState<number | null>(
    null
  );
  const [queriesSearch, setQueriesSearch] = useState("");

  let addSelfUser = true;

  useEffect(() => {
    if (visible) {
      setLoading(true);
      fetchUsers({
        type: 1,
        callback: (userList) => {
          // Do not clear preselected users in edit mode; we'll merge any missing assigned
          // users into options below so they render properly in the multi-select.
          setLoading(false);
        },
      } as any);
      // Fetch first page of queries list to populate Query select
      (async () => {
        try {
          setQueriesLoading(true);
          setQueriesPage(1);
          const resp = await fetchQueriesService({ page: 1, search: "" });
          const list = resp?.data || resp?.result || [];
          const options = (list || []).map((q: any) => ({
            label: q.query_no,
            value: q.id,
          }));
          setQueriesOptions(options);
          setQueriesTotalPages(resp?.last_page || null);
        } catch (err) {
          console.warn("Failed to fetch queries for select:", err);
          setQueriesOptions([]);
        } finally {
          setQueriesLoading(false);
        }
      })();
    }
  }, [visible]);

  // Debounced search for queries
  useEffect(() => {
    const t = setTimeout(() => {
      (async () => {
        try {
          setQueriesLoading(true);
          const resp = await fetchQueriesService({
            page: 1,
            search: queriesSearch,
          });
          const list = resp?.data || resp?.result || [];
          const options = (list || []).map((q: any) => ({
            label: q.query_no,
            value: q.id,
          }));
          setQueriesOptions(options);
          setQueriesPage(1);
          setQueriesTotalPages(resp?.last_page || null);
        } catch (err) {
          console.warn("Query search failed", err);
        } finally {
          setQueriesLoading(false);
        }
      })();
    }, 400);
    return () => clearTimeout(t);
  }, [queriesSearch]);

  const loadMoreQueries = async () => {
    if (!queriesTotalPages) return;
    if (queriesPage >= queriesTotalPages) return;
    const next = queriesPage + 1;
    try {
      setQueriesLoading(true);
      const resp = await fetchQueriesService({
        page: next,
        search: queriesSearch,
      });
      const list = resp?.data || resp?.result || [];
      const options = (list || []).map((q: any) => ({
        label: q.query_no,
        value: q.id,
      }));
      setQueriesOptions((prev) => [...prev, ...options]);
      setQueriesPage(next);
    } catch (err) {
      console.warn("Failed to load more queries", err);
    } finally {
      setQueriesLoading(false);
    }
  };

  useEffect(() => {
    updateRemindDateTime();
  }, [remindOn, remindTime, snoozeTime]);

  useEffect(() => {
    if (!visible) {
      // Reset form when panel is closed
      setModel({});
      setRemindOn(null);
      setRemindTime("");
      setSnoozeTime("None");
    }
  }, [visible]);

  const dispatch = useAppDispatch();

  // Effect to populate form data in edit mode
  useEffect(() => {
    if (visible && editMode && editReminderData) {
      // Populate form with existing reminder data
      const assignedIds: number[] = (editReminderData?.assigned_users || [])
        .map((u: any) => u?.id)
        .filter((id: any) => id !== undefined && id !== null);

      // Derive query id/label for prefill (support multiple shapes)
      const existingQueryId =
        editReminderData?.query_id ??
        editReminderData?.query?.id ??
        editReminderData?.query?.ID ??
        null;
      const existingQueryLabel =
        editReminderData?.query?.query_no ??
        editReminderData?.query_no ??
        (existingQueryId ? String(existingQueryId) : "");

      setModel({
        reminder_type: editReminderData.reminder_type,
        title: editReminderData.title,
        description: editReminderData.description,
        // In multi-assign permission, prefill all assigned users; otherwise, single fallback
        userid: checkPermission(reminderAPIUrls?.assignReminderMultiple)
          ? assignedIds
          : assignedIds?.[0] || editReminderData.created_by?.id || "",
        // Prefill Query (Optional)
        query_id: existingQueryId ? String(existingQueryId) : undefined,
      });

      // Ensure the selected query exists in the options so it renders as selected
      if (existingQueryId && existingQueryLabel) {
        setQueriesOptions((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const exists = list.some(
            (o) => String(o.value) === String(existingQueryId)
          );
          if (exists) return list;
          return [
            {
              label: existingQueryLabel as string,
              value: String(existingQueryId),
            },
            ...list,
          ];
        });
      }

      // Set reminder date and time
      if (editReminderData.remind_on) {
        const remindOnDate = moment(editReminderData.remind_on);
        setRemindOn(remindOnDate.toDate());
        setRemindTime(remindOnDate.format("HH:mm"));
      }

      // Set snooze time based on remind_before_minutes
      const minutes = editReminderData.remind_before_minutes || 0;
      const minutesToLabel: Record<number, string> = {
        0: "None",
        5: "5 Minutes",
        10: "10 Minutes",
        15: "15 Minutes",
        30: "30 Minutes",
        60: "1 Hour",
        120: "2 Hours",
        240: "4 Hours",
        1440: "1 Day",
        2880: "2 Days",
        10080: "1 Week",
      };
      setSnoozeTime(minutesToLabel[minutes] || "None");
    }
  }, [visible, editMode, editReminderData]);

  // After queries list loads/changes, ensure edit reminder's query option is present
  useEffect(() => {
    if (!(visible && editMode && editReminderData)) return;
    const existingQueryId =
      editReminderData?.query_id ??
      editReminderData?.query?.id ??
      editReminderData?.query?.ID ??
      null;
    const existingQueryLabel =
      editReminderData?.query?.query_no ??
      editReminderData?.query_no ??
      (existingQueryId ? String(existingQueryId) : "");
    if (!existingQueryId || !existingQueryLabel) return;
    const exists = (queriesOptions || []).some(
      (o) => String(o.value) === String(existingQueryId)
    );
    if (!exists) {
      setQueriesOptions((prev) => [
        { label: existingQueryLabel as string, value: String(existingQueryId) },
        ...(Array.isArray(prev) ? prev : []),
      ]);
    }
  }, [visible, editMode, editReminderData, queriesOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const root = JSON.parse(localStorage.getItem("persist:root") || "{}");

    // authUser is a string, parse it again
    const authUser = root?.authUser ? JSON.parse(root.authUser) : null;

    // Get the user ID
    const loggedInUserId = authUser?.id;
    console.log("Logged-in User ID:", loggedInUserId);

    // Correct userId logic
    // const modelUserId = checkPermission(reminderAPIUrls?.assignReminderMultiple)
    //   ? Array.isArray(model.userid)
    //     ? model.userid
    //     : model.userid
    //     ? [model.userid]
    //     : [] // no selected users, optional handling
    //   : [loggedInUserId];

    const modelUserId = checkPermission(reminderAPIUrls?.assignReminderMultiple)
      ? ([] as number[]).concat(model.userid || [])
      : [loggedInUserId];

    if (
      !model.reminder_type ||
      !model.title ||
      !model.description ||
      // !model.userid ||
      !remindOn ||
      !remindTime
    ) {
      return;
    }

    try {
      setLoading(true);
      try {
        console.log(
          "CreateReminderPanel: dispatching reminders:creating",
          Date.now()
        );
        window.dispatchEvent(new CustomEvent("reminders:creating"));
      } catch (err) {
        // ignore if dispatch fails in some environments
      }

      const combinedDateTime = moment(
        `${moment(remindOn).format("YYYY-MM-DD")} ${remindTime}`
      );

      const payload = {
        query_id: model?.query_id || null,
        title: model.title,
        description: model.description,
        assigned_to: modelUserId,
        remind_on: combinedDateTime.format("YYYY-MM-DD HH:mm:ss"),
        remind_before_minutes: convertSnoozeTimeToMinutes(snoozeTime),
        reminder_type: model.reminder_type,
      };

      let response;

      if (editMode && editReminderId) {
        response = await updateReminderService(editReminderId, payload);
      } else {
        // Create new reminder
        response = await createReminderService(payload);
      }

      // Handle successful response
      setLoading(false);

      // Normalize created data from response shapes
      let createdData = response?.result || response?.data || response || null;

      console.log(
        createdData,
        "createdDatacreatedDatacreatedDatacreatedDatacreatedData"
      );

      const isSuccess =
        (response && (response.status === true || response.success === true)) ||
        (!!createdData &&
          (typeof createdData === "object" ||
            typeof createdData === "string" ||
            typeof createdData === "number"));

      if (isSuccess) {
        // If backend returned only an id or primitive, try fetch by id to get full reminder
        try {
          let idCandidate: any = null;
          if (
            typeof createdData === "string" ||
            typeof createdData === "number"
          ) {
            idCandidate = createdData;
          } else if (createdData && (createdData.id || createdData.ID)) {
            idCandidate = createdData.id || createdData.ID;
          }

          if (idCandidate) {
            try {
              const full = await fetchReminderByIdService(String(idCandidate));
              const fullData = full?.result || full?.data || full || null;
              if (fullData && typeof fullData === "object")
                createdData = fullData;
            } catch (err) {
              console.warn(
                "Failed to fetch created reminder by id",
                idCandidate,
                err
              );
            }
          }
        } catch (err) {
          console.warn("Error resolving created reminder:", err);
        }

        // Reset form and close
        setModel({});
        setRemindOn(null);
        setRemindTime("");
        setSnoozeTime("None");
        setVisible(false);

        console.log(createdData, "createdData");

        // First, notify parent to trigger refresh
        if (onSuccess) {
          try {
            onSuccess(createdData || response);
          } catch (err) {
            console.warn("onSuccess callback error:", err);
          }
        }

        // Then emit the 'reminders:created' event after a small delay to ensure
        // the backend has processed the creation and the parent has started refreshing
        setTimeout(() => {
          try {
            console.log(
              "CreateReminderPanel: dispatching reminders:created",
              Date.now()
            );
            window.dispatchEvent(
              new CustomEvent("reminders:created", { detail: createdData })
            );
            // Navigate (or re-navigate) to /reminders with a timestamp query to
            // force the Reminders page to notice a location change and refresh.
            try {
              navigate(`/reminders?refresh=${Date.now()}`);
            } catch (navErr) {
              // ignore navigation errors in non-router contexts
            }
          } catch (err) {
            // ignore
          }
        }, 100);
      } else {
        alert(
          `Failed to ${
            editMode ? "update" : "create"
          } reminder. Please try again.`
        );
      }
    } catch (error) {
      setLoading(false);
      console.error(
        `Error ${editMode ? "updating" : "creating"} reminder:`,
        error
      );
      alert(
        `Failed to ${
          editMode ? "update" : "create"
        } reminder. Please try again.`
      );
    }
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    setModel((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e?.target?.name;
    const value = e?.target?.value;
    setModel({ ...model, [name]: value });
  };

  const handleDateChange = (value: any) => {
    if (value) {
      setModel((previous) => {
        return {
          ...previous,
          remindOn: moment(value).format("YYYY-MM-DD"),
        };
      });
    }
  };

  const updateRemindDateTime = () => {
    if (remindOn && remindTime) {
      const combinedDateTime = moment(
        `${moment(remindOn).format("YYYY-MM-DD")} ${remindTime}`
      );
      setModel((previous) => ({
        ...previous,
        remindOn: combinedDateTime.format("YYYY-MM-DD HH:mm:ss"),
        remindTime: remindTime,
        snoozeTime: snoozeTime,
      }));
    }
  };

  const convertSnoozeTimeToMinutes = (snoozeText: string): number => {
    switch (snoozeText) {
      case "None":
        return 0;
      case "5 Minutes":
        return 5;
      case "10 Minutes":
        return 10;
      case "15 Minutes":
        return 15;
      case "30 Minutes":
        return 30;
      case "1 Hour":
        return 60;
      case "2 Hours":
        return 120;
      case "4 Hours":
        return 240;
      case "1 Day":
        return 1440;
      case "2 Days":
        return 2880;
      case "1 Week":
        return 10080;
      case "2 Days":
        return 2880;
      case "1 Week":
        return 10080;
      default:
        return 0;
    }
  };

  const selfUser = addSelfUser
    ? [
        {
          name: authUser?.name,
          last_name: authUser?.last_name,
          id: authUser?.id,
        },
      ]
    : [];

  // Ensure assigned users from editReminderData are present in options so value renders selected
  const assignedUsersFromEdit = (editReminderData?.assigned_users || [])
    .map((u: any) => ({
      name: u?.name || u?.first_name || "",
      last_name: u?.last_name || "",
      id: u?.id,
    }))
    .filter((u: any) => u?.id !== undefined && u?.id !== null);

  // Deduplicate by id when merging
  const mergedUsers = (() => {
    const byId = new Map<number, any>();
    [...selfUser, ...users, ...assignedUsersFromEdit].forEach((u: any) => {
      const key = Number(u?.id);
      if (!Number.isNaN(key) && !byId.has(key)) byId.set(key, u);
    });
    return Array.from(byId.values());
  })();

  const userOptions = mergedUsers?.map((item) => {
    return {
      label: `${item?.name || ""} ${item?.last_name || ""}`,
      value: item?.id,
    };
  });

  const reminderTypes = [
    { value: "follow_up", label: "Follow Up" },
    { value: "call", label: "Call" },
    { value: "email", label: "Email" },
    { value: "meeting", label: "Meeting" },
  ];

  const currentDate: any = moment(new Date());

  return (
    <>
      <SlideoverModal
        className="p-2"
        visible={visible}
        onClose={() => {
          setVisible(false);
          setModel({});
          setRemindOn(null);
          setRemindTime("");
          setSnoozeTime("None");
        }}
        headerTitle={editMode ? "Edit Reminder" : "Create Reminder"}
      >
        <form className="flex flex-col h-full p-5" onSubmit={handleSubmit}>
          <div className="w-full mb-3">
            <FormLabel className="">Reminder Type</FormLabel>
            <ReactSelect
              className="w-full"
              aria-label=".form-select-lg example"
              value={model?.reminder_type}
              name="reminder_type"
              onChange={(_, value) => {
                onChangeInput({
                  target: {
                    name: "reminder_type",
                    value: value,
                  },
                });
              }}
              required
              placeholder="--Select--"
              options={reminderTypes}
            />
          </div>

          <div className="w-full mb-3">
            <FormLabel className="">Title</FormLabel>
            <FormInput
              type="text"
              name="title"
              value={model?.title || ""}
              onChange={onChangeInput}
              className="w-full"
              placeholder="Title"
              aria-label="Title"
              required
            />
          </div>

          <div className="w-full mb-3">
            <FormLabel className="">Description</FormLabel>
            <FormInput
              type="text"
              name="description"
              value={model?.description || ""}
              onChange={onChangeInput}
              className="w-full"
              placeholder="Description"
              aria-label="Description"
              required
            />
          </div>
          {/* {checkPermission(reminderAPIUrls?.assignReminderMulti) && (
            <div className="w-full mb-3">
              <FormLabel>Assign to</FormLabel>
              <ReactSelect
                className="flex-1 w-full mt-3 xl:mt-0"
                name="userid"
                placeholder="--Select--"
                isMulti
                required
                value={model?.userid}
                onChange={(_, value) =>
                  handleSelectChange({
                    target: { name: "userid", value: value as any },
                  } as any)
                }
                options={userOptions}
              />
            </div>
          )} */}

          {checkPermission(reminderAPIUrls?.assignReminderMultiple) && (
            <div className="w-full mb-3">
              <FormLabel>Assign to</FormLabel>
              <ReactSelect
                className="flex-1 w-full mt-3 xl:mt-0"
                name="userid"
                placeholder="--Select--"
                isMulti
                required
                value={model?.userid}
                onChange={(_, value) =>
                  handleSelectChange({
                    target: { name: "userid", value: value as any },
                  } as any)
                }
                options={userOptions}
              />
            </div>
          )}

          <div className="w-full mb-3">
            <FormLabel>Remind On</FormLabel>
            <div className="flex gap-3">
              <div className="flex-1">
                <DatePicker
                  className="border border-[#CBD5E1] rounded-md leading-5 text-sm w-full"
                  placeholderText="Select Date"
                  selected={remindOn ? new Date(remindOn) : null}
                  minDate={new Date()}
                  onChange={(value: Date | null) => setRemindOn(value)}
                />
              </div>
              <div className="flex-1">
                <FormInput
                  type="time"
                  name="remind_time"
                  value={remindTime}
                  onChange={(e) => setRemindTime(e.target.value)}
                  className="w-full"
                  placeholder="Select Time"
                  aria-label="Remind Time"
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Remind Before
            </label>
            <FormSelect
              value={snoozeTime}
              onChange={(e) => setSnoozeTime(e.target.value)}
              className="w-full"
            >
              <option value="None">None (0 minutes)</option>
              <option value="15 Minutes">15 Minutes</option>
              <option value="30 Minutes">30 Minutes</option>
              <option value="1 Hour">1 Hour</option>
              <option value="2 Hours">2 Hours</option>
              <option value="4 Hours">4 Hours</option>
              <option value="1 Day">1 Day</option>
              <option value="2 Days">2 Days</option>
              <option value="1 Week">1 Week</option>
            </FormSelect>
          </div>

          <div className="w-full mb-3">
            <FormLabel className="">Query (Optional)</FormLabel>
            <ReactSelect
              className="w-full"
              aria-label=".form-select-lg example"
              value={model?.query_id || model?.query}
              name="query_id"
              onChange={(_, value, action) => {
                // if user selected the load more pseudo-option
                if (value === "__load_more__") {
                  loadMoreQueries();
                  return;
                }
                onChangeInput({
                  target: {
                    name: "query_id",
                    value: value,
                  },
                });
              }}
              onInputChange={(val) => setQueriesSearch(val as string)}
              placeholder="--Select--"
              options={queriesOptions?.concat(
                queriesPage < (queriesTotalPages || 0)
                  ? [{ label: "Load more...", value: "__load_more__" }]
                  : []
              )}
              onMenuScrollToBottom={loadMoreQueries}
            />
          </div>

          <div className="absolute bottom-0 right-0 py-5 px-7">
            <Button
              className="w-full px-10 md:w-auto text-white bg-primary mt-auto ml-auto"
              type="submit"
              disabled={loading}
              loading={loading}
            >
              <Lucide icon="Check" className=" w-4 h-4 mr-2 -ml-2" />
              Submit
            </Button>
          </div>
        </form>
      </SlideoverModal>
    </>
  );
}

const mapStateToProps = ({ authUser, users }: RootState) => ({
  authUser: authUser,
  users: users?.data || [],
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchUsers: (payload: any) => dispatch(fetchAllUsers(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(Component);
